import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { del, get, post, qs } from '../api/client.js';
import { useAuth } from '../api/auth.js';
import {
  Badge,
  Card,
  ConfirmDialog,
  Empty,
  Failure,
  Field,
  Loading,
  Modal,
  Notice,
  Pagination,
} from '../components/ui.js';

interface Exception {
  id: string;
  date: string;
  kind: string;
  school_id: string | null;
  school_name: string | null;
  shift_id: string | null;
  template_id: string | null;
  template_name: string | null;
  label: string;
  scope: string;
}

interface Page<T> {
  items: T[];
  page: number;
  page_size: number;
  total: number;
  total_pages: number;
}

export function CalendarPage(): JSX.Element {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [showForm, setShowForm] = useState(false);
  const [toDelete, setToDelete] = useState<Exception | null>(null);
  const [error, setError] = useState<string | null>(null);

  const exceptions = useQuery({
    queryKey: ['calendar', page],
    queryFn: () => get<Page<Exception>>(`/calendar-exceptions${qs({ page, page_size: 50 })}`),
  });

  const schools = useQuery({
    queryKey: ['schools', 'all'],
    queryFn: () => get<Page<{ id: string; name: string }>>('/schools?page_size=200'),
  });

  const templates = useQuery({
    queryKey: ['schedules'],
    queryFn: () => get<Page<{ id: string; name: string }>>('/schedules?page_size=100'),
  });

  const create = useMutation({
    mutationFn: (body: Record<string, unknown>) => post('/calendar-exceptions', body),
    onSuccess: () => {
      setShowForm(false);
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['calendar'] });
    },
    onError: (e: Error) => setError(e.message),
  });

  const remove = useMutation({
    mutationFn: (id: string) => del(`/calendar-exceptions/${id}`),
    onSuccess: () => {
      setToDelete(null);
      void queryClient.invalidateQueries({ queryKey: ['calendar'] });
    },
    onError: (e: Error) => setError(e.message),
  });

  return (
    <>
      {error ? <Notice tone="danger">{error}</Notice> : null}

      <Card
        title="تقويم الاستثناءات والعطل"
        subtitle={`${exceptions.data?.total ?? 0} استثناء`}
        actions={
          can('calendar.manage') ? (
            <button className="primary" onClick={() => setShowForm(true)}>
              إضافة استثناء
            </button>
          ) : null
        }
      >
        <Notice tone="info">
          العطلة تعطّل أحداث اليوم أو الدوام المحدد. الأحداث المستقلة التي فُعّل فيها خيار «يعمل في
          العطل» فقط هي التي تستمر. تظهر آثار أي تعديل بعد إعادة النشر.
        </Notice>

        {exceptions.isLoading ? (
          <Loading />
        ) : exceptions.error ? (
          <Failure error={exceptions.error} onRetry={() => void exceptions.refetch()} />
        ) : exceptions.data!.items.length === 0 ? (
          <Empty title="لا توجد استثناءات" hint="أضف العطل الرسمية أو جداول الامتحانات الخاصة." />
        ) : (
          <>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>التاريخ</th>
                    <th>النوع</th>
                    <th>النطاق</th>
                    <th>القالب البديل</th>
                    <th>الوصف</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {exceptions.data!.items.map((e) => (
                    <tr key={e.id}>
                      <td className="mono">{e.date}</td>
                      <td>
                        {e.kind === 'holiday' ? (
                          <Badge tone="warn" icon="⊘">
                            عطلة
                          </Badge>
                        ) : (
                          <Badge tone="info" icon="◷">
                            جدول خاص
                          </Badge>
                        )}
                      </td>
                      <td>{e.school_name ?? 'كل مدارس المديرية'}</td>
                      <td>{e.template_name ?? '—'}</td>
                      <td>{e.label}</td>
                      <td className="actions">
                        {can('calendar.manage') ? (
                          <button className="ghost" onClick={() => setToDelete(e)}>
                            حذف
                          </button>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination
              page={exceptions.data!.page}
              totalPages={exceptions.data!.total_pages}
              total={exceptions.data!.total}
              onChange={setPage}
            />
          </>
        )}
      </Card>

      {showForm ? (
        <ExceptionForm
          schools={schools.data?.items ?? []}
          templates={templates.data?.items ?? []}
          busy={create.isPending}
          onCancel={() => setShowForm(false)}
          onSubmit={(body) => create.mutate(body)}
        />
      ) : null}

      {toDelete ? (
        <ConfirmDialog
          title="حذف الاستثناء"
          danger
          busy={remove.isPending}
          confirmLabel="حذف"
          message={
            <>
              سيُحذف استثناء <span className="mono">{toDelete.date}</span> «{toDelete.label}». لن
              يتغير أي جدول منشور حتى تعيد النشر.
            </>
          }
          onCancel={() => setToDelete(null)}
          onConfirm={() => remove.mutate(toDelete.id)}
        />
      ) : null}
    </>
  );
}

function ExceptionForm({
  schools,
  templates,
  busy,
  onCancel,
  onSubmit,
}: {
  schools: Array<{ id: string; name: string }>;
  templates: Array<{ id: string; name: string }>;
  busy: boolean;
  onCancel: () => void;
  onSubmit: (body: Record<string, unknown>) => void;
}): JSX.Element {
  const [form, setForm] = useState({
    date: '',
    kind: 'holiday' as 'holiday' | 'custom_schedule',
    school_id: '',
    template_id: '',
    label: '',
  });

  const valid =
    form.date && form.label && (form.kind === 'holiday' || Boolean(form.template_id));

  return (
    <Modal
      title="إضافة استثناء"
      onClose={onCancel}
      footer={
        <>
          <button onClick={onCancel} disabled={busy}>
            إلغاء
          </button>
          <button
            className="primary"
            disabled={busy || !valid}
            onClick={() =>
              onSubmit({
                date: form.date,
                kind: form.kind,
                school_id: form.school_id || null,
                template_id: form.kind === 'custom_schedule' ? form.template_id : null,
                shift_id: null,
                label: form.label.trim(),
              })
            }
          >
            {busy ? 'جارٍ الحفظ…' : 'حفظ'}
          </button>
        </>
      }
    >
      <Field label="التاريخ" hint="بالتوقيت المحلي للمدرسة">
        <input
          type="date"
          className="mono"
          value={form.date}
          onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
        />
      </Field>
      <Field label="النوع">
        <select
          value={form.kind}
          onChange={(e) =>
            setForm((f) => ({ ...f, kind: e.target.value as 'holiday' | 'custom_schedule' }))
          }
        >
          <option value="holiday">عطلة (تعطيل الأحداث)</option>
          <option value="custom_schedule">جدول خاص (قالب بديل لهذا اليوم)</option>
        </select>
      </Field>
      {form.kind === 'custom_schedule' ? (
        <Field label="القالب البديل">
          <select
            value={form.template_id}
            onChange={(e) => setForm((f) => ({ ...f, template_id: e.target.value }))}
          >
            <option value="">اختر قالبًا</option>
            {templates.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
        </Field>
      ) : null}
      <Field label="النطاق">
        <select
          value={form.school_id}
          onChange={(e) => setForm((f) => ({ ...f, school_id: e.target.value }))}
        >
          <option value="">كل مدارس المديرية</option>
          {schools.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>
      </Field>
      <Field label="الوصف">
        <input
          value={form.label}
          onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
          placeholder="مثل: عطلة رسمية"
        />
      </Field>
    </Modal>
  );
}
